import '../styles/style.scss';

import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { ModalContextProvider } from 'components/context/modal';
import { DataContextProvider } from 'data/context/data';
import { CartContextProvider } from 'ecommerce/context/cart';
import { UserContextProvider } from 'ecommerce/context/user';
import { EditorContextProvider } from 'editor/context/editorContext';

import AuthCookie from 'ecommerce/lib/authCookie';
import DebugBanner from 'ecommerce/components/debugBanner';

import AppVersion from 'data/components/appVersion';
import getInitialAppData from 'data/lib/getInitialAppData';

import SETTINGS, { updateSettings } from '../settings';
import PRODUCT_EDITOR_CONFIG, { PRODUCT_EDITOR_ALTERNATIVES } from '../specifications/editors';
import PRODUCT_SPECIFICATION from '../specifications/products';
import PRODUCT_CATEGORIES from '../specifications/categories';
import REDIRECTS from '../specifications/redirects.json';

import tracker from '../lib/tracker';
import ErrorPage from './500';
import MaintenancePage from './_maintenance';

const Scripts = dynamic(() => import('../components/document/scripts'), {
  loading: () => null,
  ssr: false,
});

const MyCanvasApp = ({ Component, pageProps, errorCode, errorBody, ...appProps }) => {
  const router = useRouter();

  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const debug = appProps.debug || SETTINGS.DEBUG;

  useEffect(() => {
    updateSettings({ DEBUG: appProps.debug });
  }, []);

  useEffect(() => {
    const handleStart = path => {
      if (router.route !== path) {
        setIsRouteChanging(true);
      }
    };
    const handleStop = () => {
      setIsRouteChanging(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  if (errorCode) {
    if (errorCode === 503) {
      return <MaintenancePage />;
    }
    return <ErrorPage code={errorCode} body={errorBody} />;
  }

  return (
    <>
      {!debug && <Scripts onAppInit={tracker.onAppInit} onPageView={tracker.pageView} />}

      <ModalContextProvider isRouteChanging={isRouteChanging} progressBarColor="#27326C">
        <AppVersion appName="MyCanvas" buildId={appProps.buildId} supportedUrls={['/editor']} />

        <DataContextProvider
          productsSpecification={PRODUCT_SPECIFICATION}
          categoriesSpecification={PRODUCT_CATEGORIES}
          realCountryCode={appProps.realCountryCode}
          countryCode={appProps.countryCode}
          skuProducts={appProps.skuProducts}
          productsData={appProps.productsData}
          categoriesData={appProps.categoriesData}
          countries={appProps.countries}
          banner={appProps.banner}
        >
          <UserContextProvider
            storageKey="mycanvas_user_next"
            accessToken={appProps.accessToken}
            onUserSet={tracker.userSet}
            ancestryCode={router.query.code}
            ancestryState={router.query.state}
            onCustomEvent={tracker.onCustomEvent}
          >
            <CartContextProvider
              storageKey="mycanvas_cart_next"
              promoCode={appProps.query.coupon}
              onCartItemAdd={tracker.cartItemAdd}
              onCartItemUpgrade={tracker.editorUpgradeProduct}
              onCustomEvent={tracker.onCustomEvent}
            >
              <EditorContextProvider
                platform="mycanvas"
                storageKey="mycanvas_editor_next"
                defaultEditor="new"
                expiration={5}
                configuration={PRODUCT_EDITOR_CONFIG}
                alternatives={PRODUCT_EDITOR_ALTERNATIVES}
                whiteLabeledDomains={SETTINGS.EDITOR_WHITE_LABELED_DOMAINS}
                onChoosePhotos={tracker.editorChoosePhotos}
                onUploadPhotos={tracker.editorUploadPhotos}
                onPreviewPrints={tracker.editorPreviewPrints}
                onCustomEvent={tracker.onCustomEvent}
              >
                <Component {...pageProps} />

                {debug && <DebugBanner />}
              </EditorContextProvider>
            </CartContextProvider>
          </UserContextProvider>
        </DataContextProvider>
      </ModalContextProvider>
    </>
  );
};

MyCanvasApp.getInitialProps = async ({ Component, ctx }) => {
  const responseError = (code, body) => {
    if (ctx.res) {
      ctx.res.statusCode = code;
    }
    return {
      errorCode: code,
      errorBody: body,
    };
  };

  try {
    //
    // 1, Get accessToken
    //
    const { value: accessToken, debug: accessTokenDebug } = AuthCookie.get(ctx);

    //
    // 2. Get all necessary data, everything is based on realCountryCode
    //
    const {
      countryCode,
      realCountryCode,
      countries,
      skuProducts,
      productsData,
      categoriesData,
      banner,
    } = await getInitialAppData(ctx, {
      defaultCountryCode: SETTINGS.DEFAULT_COUNTRY_CODE,
      productIds: Object.keys(PRODUCT_SPECIFICATION),
      categoryIds: PRODUCT_CATEGORIES.map(({ id }) => id),
      redirects: REDIRECTS,
    });

    //
    // 3. Prepare pageProps
    //
    let pageProps = {};
    if (Component.getInitialProps) {
      //
      // Each page receives except ctx also accessToken, countryCode and skuProducts
      //
      pageProps = await Component.getInitialProps({
        ...ctx,
        accessToken,
        countryCode,
        skuProducts,
      });
    }

    //
    // 5. Debug mode
    //
    // TODO - use cookie? routing with debug query param?
    //
    const debug = accessTokenDebug || ctx.query?.debug === 'true';

    return {
      pageProps,
      accessToken,
      countryCode,
      realCountryCode,
      countries,
      skuProducts,
      query: ctx.query,
      productsData,
      categoriesData,
      banner,
      debug,
      buildId: SETTINGS.BUILD_ID,
    };
  } catch (e) {
    return responseError(e.code ?? 500, e.message);
  }
};

export default MyCanvasApp;
